import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { sendgridApi } from '../api/sendgrid';
import { useToast } from '../contexts/ToastContext';
import SlideOverPanel from '../components/SlideOverPanel';
import {
  PaperAirplaneIcon,
  EyeIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  ChartBarIcon,
  LinkIcon,
} from '@heroicons/react/24/outline';

const STATUS_COLORS = {
  draft: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200',
  scheduled: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
  triggered: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200',
};

const STATUS_LABELS = {
  draft: 'Draft',
  scheduled: 'Scheduled',
  triggered: 'Sent',
};

function SingleSendsPage() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [previewId, setPreviewId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [expandedId, setExpandedId] = useState(null);
  const [linksId, setLinksId] = useState(null);

  // Fetch single sends list
  const { data, isLoading, error } = useQuery({
    queryKey: ['singleSends', statusFilter, page],
    queryFn: () => sendgridApi.listSingleSends({
      ...(statusFilter ? { status: statusFilter } : {}),
      page: page.toString(),
      limit: '10',
    }),
  });

  // Fetch stats for expanded item
  const { data: statsData, isLoading: statsLoading } = useQuery({
    queryKey: ['singleSendStats', expandedId],
    queryFn: () => sendgridApi.getSingleSendStats(expandedId),
    enabled: !!expandedId,
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, updates }) => sendgridApi.updateSingleSend(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['singleSends'] });
      setEditingId(null);
      setEditForm({});
      showSuccess('Single send updated');
    },
    onError: (err) => {
      showError(err.response?.data?.error || 'Failed to update');
    },
  });

  const singleSends = data?.result || [];
  const totalPages = data?.totalPages || 1;
  const totalCount = data?.totalCount || 0;

  const handleEdit = (ss) => {
    setEditingId(ss.id);
    setEditForm({
      name: ss.name || '',
    });
  };

  const handleSaveEdit = () => {
    const updates = {};
    if (editForm.name) updates.name = editForm.name;
    updateMutation.mutate({ id: editingId, updates });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const formatRate = (value, total) => {
    if (!total || total === 0) return '0%';
    return `${((value / total) * 100).toFixed(1)}%`;
  };

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800/50 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-200">Failed to load single sends: {error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <PaperAirplaneIcon className="h-7 w-7 text-obn-blue dark:text-blue-400" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Single Sends</h1>
        </div>

        {/* Status filter */}
        <div className="flex gap-2">
          {['', 'draft', 'scheduled', 'triggered'].map((status) => (
            <button
              key={status}
              onClick={() => { setStatusFilter(status); setPage(1); }}
              className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                statusFilter === status
                  ? 'bg-obn-blue text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
              }`}
            >
              {status ? STATUS_LABELS[status] : 'All'}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 animate-pulse">
              <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-2"></div>
              <div className="h-4 bg-gray-100 dark:bg-gray-700 rounded w-1/4"></div>
            </div>
          ))}
        </div>
      ) : singleSends.length === 0 ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          <PaperAirplaneIcon className="h-12 w-12 mx-auto mb-3 text-gray-300 dark:text-gray-500" />
          <p>No single sends found</p>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Pagination info */}
          <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
            <span>{totalCount} single send{totalCount !== 1 ? 's' : ''}</span>
            <span>Page {page} of {totalPages}</span>
          </div>

          {singleSends.map((ss) => (
            <div key={ss.id} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              {/* Main row */}
              <div className="p-4 flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  {editingId === ss.id ? (
                    <input
                      type="text"
                      value={editForm.name}
                      onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                      className="block w-full text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1 focus:ring-obn-blue focus:border-obn-blue dark:focus:ring-blue-400 dark:focus:border-blue-400"
                      placeholder="Campaign name"
                    />
                  ) : (
                    <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{ss.name}</h3>
                  )}
                </div>

                <div className="flex items-center gap-3 ml-4">
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${STATUS_COLORS[ss.status]}`}>
                    {STATUS_LABELS[ss.status] || ss.status}
                  </span>

                  <span className="text-xs text-gray-400 dark:text-gray-500">{formatDate(ss.updated_at)}</span>

                  <div className="flex items-center gap-1">
                    {ss.status === 'triggered' && (
                      <>
                        <button
                          onClick={() => toggleExpand(ss.id)}
                          className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                          title="View stats"
                        >
                          <ChartBarIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setLinksId(linksId === ss.id ? null : ss.id)}
                          className={`p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 ${linksId === ss.id ? 'text-obn-blue bg-gray-100 dark:text-blue-400 dark:bg-gray-700' : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'}`}
                          title="Link click stats"
                        >
                          <LinkIcon className="h-4 w-4" />
                        </button>
                      </>
                    )}

                    <button
                      onClick={() => setPreviewId(previewId === ss.id ? null : ss.id)}
                      className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                      title="Preview"
                    >
                      <EyeIcon className="h-4 w-4" />
                    </button>

                    {ss.status === 'draft' && (
                      editingId === ss.id ? (
                        <div className="flex gap-1">
                          <button
                            onClick={handleSaveEdit}
                            disabled={updateMutation.isPending}
                            className="p-1.5 rounded hover:bg-green-50 dark:hover:bg-green-900/30 text-green-600 dark:text-green-400"
                          >
                            <CheckIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 dark:text-gray-500"
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleEdit(ss)}
                          className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                          title="Edit"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                      )
                    )}
                  </div>
                </div>
              </div>

              {/* Stats panel */}
              {expandedId === ss.id && (
                <div className="border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 px-4 py-3">
                  {statsLoading ? (
                    <div className="text-sm text-gray-400 dark:text-gray-500">Loading stats...</div>
                  ) : statsData?.stats ? (
                    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4">
                      <StatBox label="Delivered" value={statsData.stats.delivered || 0} />
                      <StatBox
                        label="Open Rate"
                        value={formatRate(statsData.stats.unique_opens, statsData.stats.delivered)}
                      />
                      <StatBox
                        label="Click Rate"
                        value={formatRate(statsData.stats.unique_clicks, statsData.stats.delivered)}
                      />
                      <StatBox label="Bounces" value={statsData.stats.bounces || 0} />
                      <StatBox label="Unsubscribes" value={statsData.stats.unsubscribes || 0} />
                      <StatBox label="Spam Reports" value={statsData.stats.spam_reports || 0} />
                    </div>
                  ) : (
                    <div className="text-sm text-gray-400 dark:text-gray-500">No stats available</div>
                  )}
                </div>
              )}

              {/* Preview panel */}
              {previewId === ss.id && (
                <div className="border-t border-gray-100 dark:border-gray-700">
                  <PreviewIframe id={ss.id} name={ss.name} />
                </div>
              )}
            </div>
          ))}

          {/* Pagination controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                    p === page
                      ? 'bg-obn-blue text-white'
                      : 'border border-gray-300 text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                  }`}
                >
                  {p}
                </button>
              ))}
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}

      {/* Link stats slide-over */}
      <SlideOverPanel
        isOpen={!!linksId}
        onClose={() => setLinksId(null)}
        title={`Link Stats — ${singleSends.find((s) => s.id === linksId)?.name || ''}`}
        width="65vw"
      >
        {linksId && <LinkStatsPanel id={linksId} />}
      </SlideOverPanel>
    </div>
  );
}

function PreviewIframe({ id, name }) {
  const iframeRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let revoked = false;
    let blobUrl = null;

    sendgridApi.getSingleSendPreview(id)
      .then((html) => {
        if (revoked) return;
        const blob = new Blob([html], { type: 'text/html' });
        blobUrl = URL.createObjectURL(blob);
        if (iframeRef.current) {
          iframeRef.current.src = blobUrl;
        }
        setLoading(false);
      })
      .catch((err) => {
        if (!revoked) {
          setError(err.message || 'Failed to load preview');
          setLoading(false);
        }
      });

    return () => {
      revoked = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [id]);

  if (error) {
    return <div className="p-4 text-sm text-red-600 dark:text-red-400">Preview error: {error}</div>;
  }

  return (
    <>
      {loading && <div className="p-4 text-sm text-gray-400 dark:text-gray-500">Loading preview...</div>}
      <iframe
        ref={iframeRef}
        title={`Preview: ${name}`}
        className={`w-full h-[500px] border-0 ${loading ? 'hidden' : ''}`}
        sandbox="allow-same-origin"
      />
    </>
  );
}

function LinkStatsPanel({ id }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['singleSendLinks', id],
    queryFn: () => sendgridApi.getSingleSendLinkStats(id),
    enabled: !!id,
  });

  const links = data?.results || [];

  if (isLoading) {
    return (
      <div className="p-6 space-y-3">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="h-5 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800/50 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-200 text-sm">Failed to load link stats: {error.message}</p>
        </div>
      </div>
    );
  }

  if (links.length === 0) {
    return (
      <div className="p-6 text-center text-sm text-gray-500 dark:text-gray-400">
        <LinkIcon className="h-10 w-10 mx-auto mb-2 text-gray-300 dark:text-gray-500" />
        No link data available
      </div>
    );
  }

  // Sort by clicks descending
  const sorted = [...links].sort((a, b) => (b.clicks ?? 0) - (a.clicks ?? 0));
  const totalClicks = sorted.reduce((sum, l) => sum + (l.clicks ?? 0), 0);

  return (
    <div className="p-6">
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
        {links.length} link{links.length !== 1 ? 's' : ''} tracked &middot; {totalClicks.toLocaleString()} total clicks
      </p>
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-800">
          <tr>
            <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">URL</th>
            <th className="px-4 py-2.5 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Clicks</th>
            <th className="px-4 py-2.5 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">%</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
          {sorted.map((link, idx) => {
            const clicks = link.clicks ?? 0;
            const pct = totalClicks > 0 ? ((clicks / totalClicks) * 100).toFixed(1) : '0.0';
            return (
              <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                <td className="px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200">
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-obn-blue dark:text-blue-400 dark:hover:text-blue-300 hover:underline break-all line-clamp-2"
                    title={link.url}
                  >
                    {link.url}
                  </a>
                </td>
                <td className="px-4 py-2.5 text-sm text-gray-900 dark:text-gray-100 text-right font-medium tabular-nums">
                  {clicks.toLocaleString()}
                </td>
                <td className="px-4 py-2.5 text-sm text-gray-500 dark:text-gray-400 text-right tabular-nums">
                  {pct}%
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function StatBox({ label, value }) {
  return (
    <div className="text-center">
      <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">{value}</div>
      <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
    </div>
  );
}

export default SingleSendsPage;
